import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  X, Loader2, Save, Upload, Trash2, FileText, Home, TrendingUp,
  ScrollText, ShoppingCart, KeyRound, Users2, Building2, Eye, Wrench,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import DocumentViewer from "./DocumentViewer";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v) || 0);

const SALE_DOCS = ["Purchase deed", "Index 2", "Society certificate", "Society membership certificate",
  "Society NoC certificate", "Mortgage deed", "Mortgage certificate", "Current Search report",
  "New Search Report", "Possession certificate", "Building Plan", "New Agreement",
  "New Deed of Assignment", "Power of Attorney"];
const PURCHASE_DOCS = ["Current Agreement", "Current Purchase deed", "Current Index 2", "Society certificate",
  "Society membership certificate", "Society NoC certificate", "Current Mortgage deed",
  "Current Mortgage certificate", "Current Search report", "New Search Report",
  "Current Possession certificate", "Building Plan", "New Agreement",
  "New Deed of Assignment", "Power of Attorney"];
const TENANCY_DOCS = ["Tenant Identity", "Tenancy agreement", "Power of Attorney", "Police Verification"];
const MAINT_DOCS = ["Quotation", "Contractor Invoice", "Work Photos", "Completion Report", "Other"];
const TENANCY_FIELDS = [
  ["rent_amount", "Rent amount (₹)", "number"], ["deposit_amount", "Deposit amount (₹)", "number"],
  ["start_date", "Start date", "date"], ["end_date", "End date", "date"],
  ["first_revision_date", "First rent revision date", "date"], ["first_revision_amount", "First rent revision amount (₹)", "number"],
  ["second_revision_date", "Second rent revision date", "date"], ["second_revision_amount", "Second rent revision amount (₹)", "number"],
];
const EMPTY_TENANCY = Object.fromEntries(TENANCY_FIELDS.map(([k]) => [k, ""]));
const SCOPED = ["legal_rep", "accountant", "broker", "caretaker"];

const computeTabs = (data, role) => {
  const pls = data?.pipelines || [];
  const all = ["sale", "purchase", "tenancy", "maintenance"];
  let deal = pls.length ? all.filter((t) => t === "maintenance" || pls.includes(t)) : all;
  if (data?.allowed_pipelines) deal = deal.filter((t) => data.allowed_pipelines.includes(t));
  const hideVal = role === "broker" || role === "caretaker";
  return ["info", ...(hideVal ? [] : ["valuation"]), ...deal];
};

const TAB_META = {
  info: { label: "Info", icon: Home },
  valuation: { label: "Valuation", icon: TrendingUp },
  sale: { label: "Sale", icon: ScrollText },
  purchase: { label: "Purchase", icon: ShoppingCart },
  tenancy: { label: "Tenancy", icon: KeyRound },
  maintenance: { label: "Maintenance", icon: Wrench },
};
const TAB_TINT = {
  info: "bg-sky-100 text-sky-800 border-sky-300",
  valuation: "bg-violet-100 text-violet-800 border-violet-300",
  sale: "bg-emerald-100 text-emerald-800 border-emerald-300",
  purchase: "bg-amber-100 text-amber-800 border-amber-300",
  tenancy: "bg-rose-100 text-rose-800 border-rose-300",
  maintenance: "bg-teal-100 text-teal-800 border-teal-300",
};

const INPUT = "mt-1 w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 disabled:bg-slate-50 disabled:text-slate-600";
const BTN = "inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-all shadow-sm active:scale-[0.98]";

const emptyPartyInfo = () => ({ first_name: "", last_name: "", address: "", phone: "", email: "", company_name: "" });
const emptyParty = () => ({ party_type: "Individual", party1: emptyPartyInfo(), party2: emptyPartyInfo() });

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => resolve(e.target.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const Field = ({ label, value, onChange, type = "text", disabled, testid }) => (
  <div>
    <label className="text-xs font-medium text-slate-600">{label}</label>
    <input type={type} value={value ?? ""} disabled={disabled} data-testid={testid}
      onChange={(e) => onChange(e.target.value)} className={INPUT} />
  </div>
);

const SectionCard = ({ title, icon: Icon, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon className="h-4 w-4 text-emerald-600" />}
      <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
    </div>
    {children}
  </div>
);

function PartyEditor({ value, onChange, disabled, groupId }) {
  const v = value || emptyParty();
  const set = (patch) => onChange({ ...v, ...patch });
  const setP = (key, field, val) => set({ [key]: { ...(v[key] || emptyPartyInfo()), [field]: val } });
  const contactFields = (key) => (
    <>
      <div className="grid grid-cols-2 gap-3">
        <Field label="First Name" value={v[key]?.first_name} onChange={(x) => setP(key, "first_name", x)} disabled={disabled} testid={`${key}-first`} />
        <Field label="Last Name" value={v[key]?.last_name} onChange={(x) => setP(key, "last_name", x)} disabled={disabled} testid={`${key}-last`} />
      </div>
      <div className="mt-3"><Field label="Address" value={v[key]?.address} onChange={(x) => setP(key, "address", x)} disabled={disabled} testid={`${key}-address`} /></div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <Field label="Contact Number" value={v[key]?.phone} onChange={(x) => setP(key, "phone", x)} disabled={disabled} testid={`${key}-phone`} />
        <Field label="Email Address" type="email" value={v[key]?.email} onChange={(x) => setP(key, "email", x)} disabled={disabled} testid={`${key}-email`} />
      </div>
    </>
  );
  return (
    <div>
      <div className="flex flex-wrap gap-4 mb-4">
        {["Individual", "Joint", "Corporate"].map((t) => (
          <label key={t} className={`inline-flex items-center gap-2 text-sm ${disabled ? "text-slate-500" : "text-slate-800 cursor-pointer"}`}>
            <input type="radio" name={groupId} disabled={disabled}
              checked={v.party_type === t} onChange={() => set({ party_type: t })}
              data-testid={`party-type-${t.toLowerCase()}`} className="accent-emerald-600" />
            {t}
          </label>
        ))}
      </div>
      {v.party_type === "Corporate" && (
        <div className="mb-3"><Field label="Company Name" value={v.party1?.company_name} onChange={(x) => setP("party1", "company_name", x)} disabled={disabled} testid="party-company-name" /></div>
      )}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">{v.party_type === "Corporate" ? "Authorised Contact" : "Party 1"}</p>
        {contactFields("party1")}
      </div>
      {v.party_type === "Joint" && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">Party 2</p>
          {contactFields("party2")}
        </div>
      )}
    </div>
  );
}

function SignatureModal({ doc, onClose, onSubmit }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect(); const t = e.touches?.[0];
    return { x: ((t ? t.clientX : e.clientX) - r.left) * (c.width / r.width), y: ((t ? t.clientY : e.clientY) - r.top) * (c.height / r.height) };
  };
  const start = (e) => { drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); e.preventDefault(); };
  const move = (e) => { if (!drawing.current) return; const ctx = canvasRef.current.getContext("2d"); const { x, y } = pos(e); ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a"; ctx.lineTo(x, y); ctx.stroke(); e.preventDefault(); };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); };
  const save = async () => {
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    setBusy(true); await onSubmit(name.trim(), canvasRef.current.toDataURL("image/png")); setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="signature-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-slate-900">Sign document</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-xs text-slate-500 mb-4 truncate">{doc.doc_type} · {doc.file_name}</p>
        <label className="text-sm text-slate-600">Full name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="signature-name-input"
          className="mt-1.5 mb-3 w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
        <div className="text-sm text-slate-600 mb-1 flex items-center justify-between"><span>Signature</span><span className="text-xs text-slate-400 font-mono">Date: {today}</span></div>
        <canvas ref={canvasRef} width={400} height={160} data-testid="signature-canvas"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          className="w-full h-40 border border-dashed border-slate-300 rounded-lg bg-slate-50 touch-none cursor-crosshair" />
        <div className="mt-4 flex items-center justify-between gap-3">
          <button onClick={clear} data-testid="signature-clear-button" className="text-sm px-4 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">Clear</button>
          <button onClick={save} disabled={busy} data-testid="signature-submit-button" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2 rounded-lg disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Sign &amp; Save
          </button>
        </div>
      </div>
    </div>
  );
}

function DocumentSection({ section, docTypes, docs, canUpload, canDelete, onUpload, onDelete, onView, canRequestSign, isClientOwner, onRequestSign, onSign, busyKey }) {
  return (
    <div className="space-y-2" data-testid={`documents-${section}`}>
      {docTypes.map((dt) => {
        const files = docs.filter((d) => d.section === section && d.doc_type === dt);
        const key = `${section}:${dt}`;
        return (
          <div key={dt} className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-slate-800">{dt}</span>
              {canUpload && (
                <label className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-300 text-emerald-700 hover:bg-emerald-50 cursor-pointer transition-colors shrink-0"
                  data-testid={`upload-${section}-btn`}>
                  {busyKey === key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
                  <input type="file" accept="image/*,application/pdf,.doc,.docx" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(section, dt, f, key); e.target.value = ""; }} />
                </label>
              )}
            </div>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1.5">
                {files.map((f) => (
                  <li key={f.id} className="flex items-center justify-between gap-2 text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                    <button type="button" onClick={() => onView && onView(f)} disabled={!onView} data-testid="property-doc-item"
                      className={`flex items-center gap-1.5 text-slate-700 truncate min-w-0 text-left ${onView ? "hover:text-emerald-700" : "cursor-default"}`}>
                      <FileText className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{f.file_name}</span>
                    </button>
                    <span className="flex items-center gap-1.5 shrink-0">
                      {f.signature_status === "signed" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-700" data-testid="doc-signed-badge" title={`Signed by ${f.signature?.name || ""} on ${f.signature?.signed_date || ""}`}>Signed</span>}
                      {f.signature_status === "requested" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 border border-amber-200 text-amber-700">Awaiting sign</span>}
                      {canRequestSign && f.signature_status !== "signed" && (
                        <button type="button" onClick={() => onRequestSign(f)} data-testid={f.signature_status === "requested" ? "resend-signature-button" : "request-signature-button"} title={f.signature_status === "requested" ? "Email the client the signing request again" : "Request e-signature from client"} className="text-[10px] px-2 py-0.5 rounded-full border border-slate-300 text-emerald-700 hover:bg-emerald-50">{f.signature_status === "requested" ? "Resend" : "Request sign"}</button>
                      )}
                      {isClientOwner && f.signature_status === "requested" && (
                        <button type="button" onClick={() => onSign(f)} data-testid="sign-document-button" title="Sign electronically" className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700">Sign</button>
                      )}
                      {onView && <button type="button" onClick={() => onView(f)} title="View" data-testid="view-property-doc-button" className="text-slate-400 hover:text-emerald-700"><Eye className="h-3.5 w-3.5" /></button>}
                      {canDelete && (
                        <button type="button" onClick={() => onDelete(f.id)} data-testid="delete-property-doc-button" title="Delete" className="text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function PropertyDetailsModal({ propertyId, defaultTab, onClose, onUpdated }) {
  const { user } = useAuth();
  const [prop, setProp] = useState(null);
  const [form, setForm] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);
  const [signDoc, setSignDoc] = useState(null);
  const [tab, setTab] = useState("info");
  const scrollRef = useRef(null);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; }, [tab]);

  const canEdit = !!prop?.can_edit;
  const canUpload = !!prop?.can_upload;
  const disabled = !canEdit;
  const role = user?.role;
  const isScoped = SCOPED.includes(role);
  const infoDisabled = disabled || isScoped;
  const canEditInfo = canEdit && !isScoped;
  const hideMortgage = role === "broker" || role === "caretaker";
  const hideUtility = role === "caretaker";
  const hideTenancyDetails = role === "broker" || role === "caretaker";
  const onViewDoc = role === "broker" ? null : setViewDoc;
  const openCtx = useRef({});
  openCtx.current = { defaultTab, onClose, role };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/properties/${propertyId}`);
        setProp(data);
        setDocs(data.documents || []);
        setForm({
          address: data.address || "",
          address_line1: data.address_line1 || "",
          address_line2: data.address_line2 || "",
          address_line3: data.address_line3 || "",
          landmark: data.landmark || "",
          city: data.city || "",
          state: data.state || "",
          postcode: data.postcode || "",
          country: "India",
          owners: (data.owners || []).map((o) => ({ ...o })),
          date_of_purchase: data.date_of_purchase || "",
          date_of_sale: data.date_of_sale || "",
          purchase_price: data.purchase_price ?? "",
          sale_price: data.sale_price ?? "",
          mortgage_bank: data.mortgage_bank || "",
          mortgage_amount: data.mortgage_amount ?? "",
          mortgage_date: data.mortgage_date || "",
          property_tax_number: data.property_tax_number || "",
          electricity_id: data.electricity_id || "",
          gas_id: data.gas_id || "",
          water_id: data.water_id || "",
          valuation: data.valuation ?? "",
          previous_valuation_amount: data.previous_valuation_amount ?? "",
          previous_valuation_date: data.previous_valuation_date || "",
          purchase_property_type: data.purchase_property_type || "New",
          purchaser: data.purchaser || emptyParty(),
          seller: data.seller || emptyParty(),
          tenant: data.tenant || emptyParty(),
          tenancy_details: { ...EMPTY_TENANCY, ...(data.tenancy_details || {}) },
        });
        const avail = computeTabs(data, openCtx.current.role);
        setTab(avail.includes(openCtx.current.defaultTab) ? openCtx.current.defaultTab : "info");
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load property");
        openCtx.current.onClose();
      } finally { setLoading(false); }
    })();
  }, [propertyId]);

  const tabs = computeTabs(prop, role);

  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));

  const addOwner = () => { if ((form.owners || []).length < 3) setF({ owners: [...form.owners, { first_name: "", last_name: "", address: "", phone: "", email: "", percentage: "" }] }); };
  const setOwner = (i, k, val) => setF({ owners: form.owners.map((o, idx) => (idx === i ? { ...o, [k]: val } : o)) });
  const removeOwner = (i) => setF({ owners: form.owners.filter((_, idx) => idx !== i) });

  const save = async () => {
    for (const o of form.owners || []) {
      if (!(o.first_name || "").trim() || !(o.last_name || "").trim()) return toast.error("Each owner needs a first and last name");
    }
    setSaving(true);
    try {
      const payload = {
        address_line1: form.address_line1, address_line2: form.address_line2, address_line3: form.address_line3,
        landmark: form.landmark, city: form.city, state: form.state, postcode: form.postcode, country: "India",
        owners: (form.owners || []).map((o) => ({ ...o, percentage: o.percentage === "" || o.percentage == null ? null : Number(o.percentage) })),
        date_of_purchase: form.date_of_purchase, date_of_sale: form.date_of_sale,
        purchase_price: num(form.purchase_price), sale_price: num(form.sale_price),
        mortgage_bank: form.mortgage_bank, mortgage_amount: num(form.mortgage_amount), mortgage_date: form.mortgage_date,
        property_tax_number: form.property_tax_number, electricity_id: form.electricity_id,
        gas_id: form.gas_id, water_id: form.water_id,
        valuation: num(form.valuation), previous_valuation_amount: num(form.previous_valuation_amount),
        previous_valuation_date: form.previous_valuation_date,
        purchase_property_type: form.purchase_property_type,
        purchaser: form.purchaser, seller: form.seller, tenant: form.tenant,
        ...(hideTenancyDetails ? {} : { tenancy_details: Object.fromEntries(Object.entries(form.tenancy_details || {}).map(([k, v]) => [k, k.endsWith("_amount") ? (v === "" || v === null ? null : Number(v)) : v])) }),
      };
      const { data } = await api.patch(`/properties/${propertyId}/full`, payload);
      setProp((p) => ({ ...p, ...data }));
      onUpdated && onUpdated(data);
      toast.success("Property details saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const uploadDoc = async (section, docType, file, key) => {
    if (file.size > 5 * 1024 * 1024) return toast.error("File too large (max 5MB)");
    setBusyKey(key);
    try {
      const dataUrl = await fileToDataUrl(file);
      const { data } = await api.post(`/properties/${propertyId}/documents`, { section, doc_type: docType, file: dataUrl, file_name: file.name });
      setDocs((d) => [...d, data]);
      toast.success("Document uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setBusyKey(null); }
  };

  const deleteDoc = async (docId) => {
    try {
      await api.delete(`/properties/${propertyId}/documents/${docId}`);
      setDocs((d) => d.filter((x) => x.id !== docId));
      toast.success("Document removed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove"); }
  };

  const canDownloadDocs = ["admin", "power_user"].includes(user?.role);
  const canDeleteDocs = canDownloadDocs && canEdit;
  const canRequestSign = ["admin", "power_user", "legal_rep", "accountant"].includes(role) && canEdit;
  const isClientOwner = user?.role === "client";
  const requestSign = async (f) => {
    try {
      const { data } = await api.post(`/properties/${propertyId}/documents/${f.id}/request-signature`);
      setDocs((ds) => ds.map((d) => (d.id === f.id ? { ...d, signature_status: "requested" } : d)));
      toast.success(data.resent ? "Signing request resent to the client" : "Signature requested — the client has been emailed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not request signature"); }
  };
  const submitSign = async (name, image) => {
    try {
      const { data } = await api.post(`/properties/${propertyId}/documents/${signDoc.id}/sign`, { name, signature_image: image });
      setDocs((ds) => {
        const next = ds.map((d) => (d.id === signDoc.id ? { ...d, ...data.document } : d));
        return data.signed_document ? [...next, data.signed_document] : next;
      });
      setSignDoc(null);
      toast.success("Document signed — a signed copy was saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not sign document"); }
  };

  const cur = num(form?.valuation), prev = num(form?.previous_valuation_amount);
  const appr = cur - prev, apprPct = prev > 0 ? (appr / prev) * 100 : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 py-[4vh] bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-3xl bg-slate-50 border border-slate-200 rounded-2xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="property-details-modal">
        {loading || !form ? (
          <div className="p-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 bg-white border-b border-slate-200 shrink-0">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.2em] font-mono text-emerald-700 font-semibold">Property</p>
                <h2 className="mt-1 text-lg font-semibold text-slate-900 truncate flex items-center gap-2"><Building2 className="h-5 w-5 text-slate-400 shrink-0" /> {prop.address}</h2>
                {!canEdit && <p className="text-xs text-slate-500 mt-1">{canUpload ? "View only — you can upload documents." : "View only"}</p>}
              </div>
              <button onClick={onClose} data-testid="close-property-modal" className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>

            <div className="flex items-center gap-1 px-4 py-2 bg-white border-b border-slate-200 overflow-x-auto shrink-0">
              {tabs.map((t) => {
                const M = TAB_META[t];
                return (
                  <button key={t} onClick={() => setTab(t)} data-testid={`property-tab-${t}`}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors whitespace-nowrap ${tab === t ? TAB_TINT[t] : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100"}`}>
                    <M.icon className="h-4 w-4" /> {M.label}
                  </button>
                );
              })}
            </div>

            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4">
              {tab === "info" && (
                <>
                  <SectionCard title="Ownership" icon={Users2}>
                    <div className="space-y-3">
                      <Field label="Address line 1" value={form.address_line1} onChange={(v) => setF({ address_line1: v })} disabled={infoDisabled} testid="info-addr1" />
                      <Field label="Address line 2" value={form.address_line2} onChange={(v) => setF({ address_line2: v })} disabled={infoDisabled} testid="info-addr2" />
                      <Field label="Address line 3" value={form.address_line3} onChange={(v) => setF({ address_line3: v })} disabled={infoDisabled} testid="info-addr3" />
                      <Field label="Landmark" value={form.landmark} onChange={(v) => setF({ landmark: v })} disabled={infoDisabled} testid="info-landmark" />
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-slate-600">City</label>
                          <select value={form.city || ""} onChange={(e) => setF({ city: e.target.value })} disabled={infoDisabled} data-testid="info-city" className={INPUT}>
                            <option value="">Select city…</option>
                            {["Mumbai", "Pune", "Kolhapur"].map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <Field label="State" value={form.state} onChange={(v) => setF({ state: v })} disabled={infoDisabled} testid="info-state" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Postcode" value={form.postcode} onChange={(v) => setF({ postcode: v })} disabled={infoDisabled} testid="info-postcode" />
                        <div>
                          <label className="text-xs font-medium text-slate-600">Country</label>
                          <input value="India" disabled className={INPUT} data-testid="info-country" />
                        </div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-slate-600">Owner(s)</span>
                        {canEditInfo && <button onClick={addOwner} disabled={(form.owners || []).length >= 3} data-testid="add-owner-modal-btn" className="text-xs px-2.5 py-1 rounded-lg border border-slate-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40">+ Add owner</button>}
                      </div>
                      {(form.owners || []).length === 0 && <p className="mt-2 text-sm text-slate-400">No owners added.</p>}
                      <div className="mt-2 space-y-3">
                        {(form.owners || []).map((o, i) => (
                          <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="info-owner-row">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[11px] font-mono text-slate-500">Owner {i + 1}</span>
                              {canEditInfo && <button onClick={() => removeOwner(i)} className="text-slate-400 hover:text-rose-600"><X className="h-4 w-4" /></button>}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <Field label="First Name" value={o.first_name} onChange={(v) => setOwner(i, "first_name", v)} disabled={infoDisabled} />
                              <Field label="Last Name" value={o.last_name} onChange={(v) => setOwner(i, "last_name", v)} disabled={infoDisabled} />
                            </div>
                            {(form.owners || []).length > 1 && (
                              <div className="mt-3 max-w-[200px]">
                                <Field label="Ownership %" type="number" value={o.percentage} onChange={(v) => setOwner(i, "percentage", v)} disabled={infoDisabled} testid="owner-percentage" />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <Field label="Date of Purchase" type="date" value={form.date_of_purchase} onChange={(v) => setF({ date_of_purchase: v })} disabled={infoDisabled} testid="info-date-purchase" />
                      <Field label="Date of Sale" type="date" value={form.date_of_sale} onChange={(v) => setF({ date_of_sale: v })} disabled={infoDisabled} testid="info-date-sale" />
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <Field label="Purchase Price (₹)" type="number" value={form.purchase_price} onChange={(v) => setF({ purchase_price: v })} disabled={infoDisabled} testid="info-purchase-price" />
                      <Field label="Sale Price (₹)" type="number" value={form.sale_price} onChange={(v) => setF({ sale_price: v })} disabled={infoDisabled} testid="info-sale-price" />
                    </div>
                  </SectionCard>

                  {!hideMortgage && <SectionCard title="Mortgage" icon={Building2}>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <Field label="Mortgage Bank" value={form.mortgage_bank} onChange={(v) => setF({ mortgage_bank: v })} disabled={infoDisabled} testid="info-mortgage-bank" />
                      <Field label="Mortgage Amount (₹)" type="number" value={form.mortgage_amount} onChange={(v) => setF({ mortgage_amount: v })} disabled={infoDisabled} testid="info-mortgage-amount" />
                      <Field label="Date Mortgage Availed" type="date" value={form.mortgage_date} onChange={(v) => setF({ mortgage_date: v })} disabled={infoDisabled} testid="info-mortgage-date" />
                    </div>
                  </SectionCard>}

                  {!hideUtility && <SectionCard title="Utility" icon={FileText}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field label="Property Tax Number" value={form.property_tax_number} onChange={(v) => setF({ property_tax_number: v })} disabled={infoDisabled} testid="info-tax" />
                      <Field label="Electricity Subscriber Id" value={form.electricity_id} onChange={(v) => setF({ electricity_id: v })} disabled={infoDisabled} testid="info-electricity" />
                      <Field label="Gas Subscriber Id" value={form.gas_id} onChange={(v) => setF({ gas_id: v })} disabled={infoDisabled} testid="info-gas" />
                      <Field label="Water Supply Id" value={form.water_id} onChange={(v) => setF({ water_id: v })} disabled={infoDisabled} testid="info-water" />
                    </div>
                  </SectionCard>}
                </>
              )}

              {tab === "valuation" && (
                <SectionCard title="Valuation" icon={TrendingUp}>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Previous Valuation Amount (₹)" type="number" value={form.previous_valuation_amount} onChange={(v) => setF({ previous_valuation_amount: v })} disabled={disabled} testid="val-prev-amount" />
                    <Field label="Previous Valuation Date" type="date" value={form.previous_valuation_date} onChange={(v) => setF({ previous_valuation_date: v })} disabled={disabled} testid="val-prev-date" />
                  </div>
                  <div className="mt-3 max-w-[280px]">
                    <Field label="Current Valuation Amount (₹)" type="number" value={form.valuation} onChange={(v) => setF({ valuation: v })} disabled={disabled} testid="val-current-amount" />
                  </div>
                  <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 p-4" data-testid="val-appreciation">
                    <p className="text-xs font-medium text-emerald-800">Appreciation</p>
                    <p className={`mt-1 text-2xl font-semibold font-mono ${appr >= 0 ? "text-emerald-700" : "text-rose-600"}`}>
                      {appr >= 0 ? "+" : ""}{inr(appr)} <span className="text-sm">({apprPct >= 0 ? "+" : ""}{apprPct.toFixed(1)}%)</span>
                    </p>
                  </div>
                </SectionCard>
              )}

              {tab === "sale" && (
                <>
                  <SectionCard title="Purchaser Info" icon={Users2}>
                    <PartyEditor value={form.purchaser} onChange={(v) => setF({ purchaser: v })} disabled={disabled} groupId="purchaser-type" />
                  </SectionCard>
                  <SectionCard title="Documents" icon={FileText}>
                    <DocumentSection section="sale" docTypes={SALE_DOCS} docs={docs} canUpload={canUpload} canDelete={canDeleteDocs} onUpload={uploadDoc} onDelete={deleteDoc} onView={onViewDoc} canRequestSign={canRequestSign} isClientOwner={isClientOwner} onRequestSign={requestSign} onSign={setSignDoc} busyKey={busyKey} />
                  </SectionCard>
                </>
              )}

              {tab === "purchase" && (
                <>
                  <SectionCard title="Property Type" icon={Home}>
                    <div className="flex gap-4">
                      {["New", "Pre-owned"].map((t) => (
                        <label key={t} className={`inline-flex items-center gap-2 text-sm ${disabled ? "text-slate-500" : "text-slate-800 cursor-pointer"}`}>
                          <input type="radio" disabled={disabled} checked={form.purchase_property_type === t} onChange={() => setF({ purchase_property_type: t })} data-testid={`purchase-type-${t.toLowerCase().replace("-", "")}`} className="accent-emerald-600" /> {t}
                        </label>
                      ))}
                    </div>
                  </SectionCard>
                  <SectionCard title="Seller Info" icon={Users2}>
                    <PartyEditor value={form.seller} onChange={(v) => setF({ seller: v })} disabled={disabled} groupId="seller-type" />
                  </SectionCard>
                  <SectionCard title="Documents" icon={FileText}>
                    <DocumentSection section="purchase" docTypes={PURCHASE_DOCS} docs={docs} canUpload={canUpload} canDelete={canDeleteDocs} onUpload={uploadDoc} onDelete={deleteDoc} onView={onViewDoc} canRequestSign={canRequestSign} isClientOwner={isClientOwner} onRequestSign={requestSign} onSign={setSignDoc} busyKey={busyKey} />
                  </SectionCard>
                </>
              )}

              {tab === "tenancy" && (
                <>
                  <SectionCard title="Tenant Info" icon={Users2}>
                    <PartyEditor value={form.tenant} onChange={(v) => setF({ tenant: v })} disabled={disabled} groupId="tenant-type" />
                  </SectionCard>
                  {!hideTenancyDetails && (
                    <SectionCard title="Tenancy agreement details" icon={ScrollText}>
                      <div className="grid sm:grid-cols-2 gap-3" data-testid="tenancy-details-section">
                        {TENANCY_FIELDS.map(([k, label, type]) => (
                          <Field key={k} label={label} type={type} value={form.tenancy_details?.[k]} disabled={infoDisabled}
                            onChange={(v) => setF({ tenancy_details: { ...form.tenancy_details, [k]: v } })} testid={`tenancy-${k.replace(/_/g, "-")}`} />
                        ))}
                      </div>
                    </SectionCard>
                  )}
                  <SectionCard title="Documents" icon={FileText}>
                    <DocumentSection section="tenancy" docTypes={TENANCY_DOCS} docs={docs} canUpload={canUpload} canDelete={canDeleteDocs} onUpload={uploadDoc} onDelete={deleteDoc} onView={onViewDoc} canRequestSign={canRequestSign} isClientOwner={isClientOwner} onRequestSign={requestSign} onSign={setSignDoc} busyKey={busyKey} />
                  </SectionCard>
                </>
              )}

              {tab === "maintenance" && (
                <SectionCard title="Maintenance Documents" icon={Wrench}>
                  <DocumentSection section="maintenance" docTypes={MAINT_DOCS} docs={docs} canUpload={canUpload} canDelete={canDeleteDocs} onUpload={uploadDoc} onDelete={deleteDoc} onView={onViewDoc} canRequestSign={canRequestSign} isClientOwner={isClientOwner} onRequestSign={requestSign} onSign={setSignDoc} busyKey={busyKey} />
                </SectionCard>
              )}
            </div>

            {canEdit && (
              <div className="px-6 py-4 bg-white border-t border-slate-200 flex justify-end shrink-0">
                <button onClick={save} disabled={saving} data-testid="save-property-details-button" className={`${BTN} px-6 py-2.5 disabled:opacity-60`}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
                </button>
              </div>
            )}
            {viewDoc && <DocumentViewer doc={viewDoc} canDownload={canDownloadDocs} onClose={() => setViewDoc(null)} />}
            {signDoc && <SignatureModal doc={signDoc} onClose={() => setSignDoc(null)} onSubmit={submitSign} />}
          </>
        )}
      </div>
    </div>
  );
}
